/**
 * True when index.html already painted the welcome poster (`#hs-boot-shell`).
 * Re-rendering the same logos/dots in React would restart CSS animations and
 * look janky — so we keep the HTML shell as the visible UI for the dwell.
 */
function hasHandshakeBootShell(): boolean {
  return (
    typeof document !== "undefined" &&
    document.getElementById("hs-boot-shell") != null
  );
}

/**
 * Handshake welcome screen — matches Figma "AFTER1"
 * (Partners Master File · node 18354:2318).
 *
 * Visuals: white background, dark wordmarks (#16181d), three dark loading
 * dots. Motion: 2s looping EN↔AR logo cross-fade + staggered dot bounce
 * (opacity / scale / translateY only — compositor-friendly for webviews).
 *
 * No Lottie: Figma keyframes are reproduced in CSS.
 *
 * Local experiment: if `#hs-boot-shell` is present (from index.html), that
 * poster stays on screen and this component only mounts a test hook.
 */
export function WelcomeScreen() {
  if (hasHandshakeBootShell()) {
    // Boot shell already exposes data-testid="handshake-welcome-screen" in
    // index.html for Playwright; avoid a duplicate marker once React mounts.
    return <div className="min-h-screen" data-boot-shell="true" aria-busy="true" />;
  }

  return (
    // Same shell as app-wrapper / not-found: gray page chrome + 480px column.
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <div
        className="hs-welcome w-full md:w-[480px] mx-auto bg-white min-h-screen md:rounded-t-[32px] md:min-h-[calc(100vh-24px)] md:mt-[24px] md:shadow-xs flex flex-col"
        data-testid="handshake-welcome-screen"
      >
        <style>{welcomeStyles}</style>

        <div className="hs-stage">
          <div className="hs-logo-wrap" aria-hidden="true">
            <TamaraLogoEn className="hs-logo hs-logo--en" />
            <TamaraLogoAr className="hs-logo hs-logo--ar" />
          </div>

          <div className="hs-dots" aria-hidden="true">
            <span className="hs-dot hs-dot--1" />
            <span className="hs-dot hs-dot--2" />
            <span className="hs-dot hs-dot--3" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Figma Text/Primary */
const LOGO_FILL = "#16181d";

function TamaraLogoEn({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 97 19"
      fill={LOGO_FILL}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M43.1532 4.6001H39.202H34.7615H30.8115V8.41392V18.4353H34.7615V8.41392H39.202V18.4353H43.1532V8.41392H47.5925V18.4353H51.5437V8.41392V4.6001H47.5925H43.1532Z" />
      <path d="M6.88596 8.41266H10.6485V4.59884H6.88596V0.0749937L6.69729 0L0 5.68263V8.41266H2.93478V14.3953V18.4341H6.88596H11.0246V14.3953H6.88596V8.41266Z" />
      <path d="M23.2855 5.83729C22.0512 4.75592 20.7512 4.3374 19.2224 4.3374C15.3467 4.3374 12.6748 7.25371 12.6748 11.4038C12.6748 15.5539 15.3467 18.4702 19.2224 18.4702C20.7524 18.4702 22.0525 18.0517 23.2855 16.9703V18.4339H27.048V4.59867H23.2855V5.83729ZM19.9746 15.0676C17.7544 15.0676 16.5505 13.4601 16.5505 11.4038C16.5505 9.3475 17.7544 7.73996 19.9746 7.73996C22.1949 7.73996 23.361 9.3475 23.361 11.4038C23.361 13.4601 22.1571 15.0676 19.9746 15.0676Z" />
      <path d="M92.4457 4.59867V5.83729C91.2114 4.75592 89.9114 4.3374 88.3825 4.3374C84.5068 4.3374 81.835 7.25371 81.835 11.4038C81.835 15.5539 84.5068 18.4702 88.3825 18.4702C89.9126 18.4702 91.2126 18.0517 92.4457 16.9703V18.4339H96.2082V4.59867H92.4457ZM89.1336 15.0676C86.9133 15.0676 85.7095 13.4601 85.7095 11.4038C85.7095 9.3475 86.9133 7.73996 89.1336 7.73996C91.3538 7.73996 92.5199 9.3475 92.5199 11.4038C92.5199 13.4601 91.3161 15.0676 89.1336 15.0676Z" />
      <path d="M64.9838 5.83729C63.7495 4.75592 62.4495 4.3374 60.9206 4.3374C57.0449 4.3374 54.373 7.25371 54.373 11.4038C54.373 15.5539 57.0449 18.4702 60.9206 18.4702C62.4507 18.4702 63.7507 18.0517 64.9838 16.9703V18.4339H68.7463V4.59867H64.9838V5.83729ZM61.6729 15.0676C59.4526 15.0676 58.2488 13.4601 58.2488 11.4038C58.2488 9.3475 59.4526 7.73996 61.6729 7.73996C63.8931 7.73996 65.0592 9.3475 65.0592 11.4038C65.0592 13.4601 63.8554 15.0676 61.6729 15.0676Z" />
      <path d="M76.2744 5.9634V4.52279H72.3232V18.433H76.2744V9.80867L80.8647 8.15033V4.29902L80.7137 4.18652L76.2744 5.9634Z" />
    </svg>
  );
}

function TamaraLogoAr({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 97 29"
      fill={LOGO_FILL}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4.0654 3.36061V22.7748C4.0654 22.8931 3.967 22.9909 3.84789 22.9909H0.217511C0.0983971 22.9909 0 22.8931 0 22.7748V3.36061C0 3.24227 0.0983971 3.1445 0.217511 3.1445H3.84789C3.96959 3.1445 4.0654 3.24227 4.0654 3.36061ZM15.7152 9.70251H12.0848C11.9657 9.70251 11.8673 9.80028 11.8673 9.91863V21.5965C11.8673 22.9446 10.6917 24.0766 9.34782 24.0766H6.44248C6.28971 24.0766 6.18613 24.2284 6.23792 24.3673L7.59996 27.9667C7.63103 28.0516 7.7113 28.1056 7.80193 28.1056L9.38148 28.1133C12.9989 28.1133 15.9301 25.2009 15.9301 21.6068V9.91863C15.9327 9.79771 15.8343 9.70251 15.7152 9.70251ZM91.3134 6.28844C93.1079 6.32188 94.5709 4.86826 94.5372 3.08533C94.5061 1.4053 93.126 0.0314392 91.4351 0.000565844C89.6406 -0.0328803 88.1776 1.42074 88.2113 3.20367C88.2398 4.88627 89.6225 6.25756 91.3134 6.28844ZM85.3422 6.16494C85.3733 6.19067 85.4147 6.20096 85.4535 6.18552C86.7068 5.75073 87.5976 4.55438 87.5665 3.15222C87.5277 1.46705 86.1242 0.103477 84.4281 0.0957586C82.8822 0.0906131 81.5927 1.18919 81.3182 2.64538C81.3105 2.68397 81.326 2.72514 81.3571 2.75087L85.3422 6.16494ZM96.21 9.67936V13.5462C96.21 13.6646 96.1116 13.7624 95.9925 13.7624H79.0991C79.4124 14.5419 79.5859 15.3935 79.5859 16.2837C79.5859 19.8856 76.7608 22.8391 73.1978 23.0835L73.2392 23.117H31.7153C22.8827 23.117 19.6511 20.0862 19.6511 12.2264V3.36061C19.6511 3.24227 19.7495 3.1445 19.8686 3.1445H23.745C23.8641 3.1445 23.9625 3.24227 23.9625 3.36061V12.8593C23.9625 16.8137 25.4022 18.468 29.8198 18.8153H66.354C66.0355 18.0306 65.8594 17.1764 65.8594 16.2811C65.8594 12.6381 68.7517 9.65106 72.3769 9.47096L72.3718 9.46324H95.9925C96.1142 9.46324 96.21 9.55844 96.21 9.67936ZM75.5283 16.2682C75.5283 14.7477 74.2025 13.4974 72.6825 13.4974C71.1625 13.4974 69.9196 14.7477 69.9196 16.2682C69.9196 17.7888 71.1625 19.0674 72.6825 19.0674C74.2025 19.0674 75.5283 17.7888 75.5283 16.2682Z" />
    </svg>
  );
}

/*
 * Motion sourced from Figma get_motion_context on 18354:2318 (2s loop).
 * Only transform + opacity are animated.
 */
const welcomeStyles = `
.hs-welcome {
  /* Width/rounding come from Tailwind (same as other PPUI screens).
     Do NOT use position:fixed — that forced full-viewport width. */
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #ffffff; /* Figma Background/Light */
  transform: translateZ(0);
  contain: layout paint;
}

.hs-stage {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px; /* Figma center-brand-logo gap */
}

.hs-logo-wrap {
  position: relative;
  width: 198px;
  height: 52px;
  transform: translateZ(0);
}

.hs-logo {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  margin: auto;
  backface-visibility: hidden;
  will-change: opacity;
}

/* Figma: EN opacity [1 → 0], AR [0 → 1], 2s ease-in-out loop */
.hs-logo--en {
  animation: hsSwapEn 2s ease-in-out infinite;
}
.hs-logo--ar {
  opacity: 0;
  animation: hsSwapAr 2s ease-in-out infinite;
}

.hs-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px; /* Figma loading-indicator gap */
  height: 20px;
}

.hs-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #16181d; /* Figma Text/Primary */
  transform: translateZ(0);
  backface-visibility: hidden;
  will-change: transform, opacity;
}

/* Staggered bounce from Figma keyframe times (2s loop) */
.hs-dot--1 {
  animation: hsDot 2s ease-in-out infinite;
}
.hs-dot--2 {
  animation: hsDot 2s ease-in-out infinite;
  animation-delay: 0.2s;
}
.hs-dot--3 {
  animation: hsDot 2s ease-in-out infinite;
  animation-delay: 0.4s;
}

/* Cross-fade then reverse so the 2s loop returns cleanly (Figma 50/50 hold). */
@keyframes hsSwapEn {
  0%, 40%  { opacity: 1; }
  50%, 90% { opacity: 0; }
  100%     { opacity: 1; }
}
@keyframes hsSwapAr {
  0%, 40%  { opacity: 0; }
  50%, 90% { opacity: 1; }
  100%     { opacity: 0; }
}

/*
 * Dot: opacity 0.3 → 1 → 0.3, scale 1 → 1.15 → 1, y 0 → -5 → 0
 * (matches Figma times ~0 / 0.15 / 0.3 of the 2s cycle)
 */
@keyframes hsDot {
  0%, 100% {
    opacity: 0.3;
    transform: translate3d(0, 0, 0) scale(1);
  }
  15% {
    opacity: 1;
    transform: translate3d(0, -5px, 0) scale(1.15);
  }
  30% {
    opacity: 0.3;
    transform: translate3d(0, 0, 0) scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .hs-logo--en,
  .hs-logo--ar,
  .hs-dot { animation: none !important; }
  .hs-logo--en { opacity: 1; }
  .hs-logo--ar { opacity: 0; }
  .hs-dot { opacity: 0.3; }
  .hs-dot--1 { opacity: 1; }
}
`;
